import { Component, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { DataService } from '@app/core/services/data.service';
import { ImageService } from '@app/shared/services/image.service';

type TrackItemMapEntry = {
    tagId?: string | null;
    tagName?: string | null;
    tagCode?: string | null;
    zoneName?: string | null;
    floorName?: string | null;
    buildingName?: string | null;
    lastSeen?: string | null;
};

type TagSeed = {
    tagId: string;
    tagName: string | null;
    tagCode: string | null;
};

type TagHistoryEntry = {
    locationText: string | null;
    zoneName: string | null;
    floorName: string | null;
    buildingName: string | null;
    firstSeenAt: string | null;
    lastSeenAt: string | null;
    durationText: string | null;
};

type TagPropertyEntry = {
    name: string;
    value: string;
    readAt: string | null;
};

type TagPanel = {
    tagId: string;
    tagName: string | null;
    tagCode: string | null;
    label: string;
    lastLocationText: string | null;
    lastSeenAt: string | null;
    history: TagHistoryEntry[];
    properties: TagPropertyEntry[];
};

@Component({
    selector: 'app-nawizard-user-profile-card',
    templateUrl: './nawizard-user-profile-card.component.html',
    styleUrls: ['./nawizard-user-profile-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NawizardUserProfileCardComponent implements OnInit, OnChanges {
    @Input() personId!: string;
    @Input() displayName: string | null = null;

    loading = false;
    error: string | null = null;

    photoUrl: string | null = null;
    zoneName: string | null = null;
    activeTagCount = 0;
    typeId: string | null = null;

    tags: TagPanel[] = [];
    selectedTagId: string | null = null;
    selectedTag: TagPanel | null = null;
    isExpanded = false;
    activeBackTab: 'summary' | 'history' | 'properties' = 'summary';

    private requestSeq = 0;

    constructor(
        private dataService: DataService,
        private imageService: ImageService,
        private router: Router,
    ) { }

    ngOnInit(): void {
        void this.load();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['personId'] && !changes['personId'].firstChange) {
            void this.load();
        }
    }

    async openProfile(): Promise<void> {
        if (!this.personId) return;
        await this.router.navigate(['/track-items', 'profile', this.personId]);
    }

    selectTag(tagId: string): void {
        if (!tagId || this.selectedTagId === tagId) return;
        this.selectedTagId = tagId;
        this.selectedTag = this.tags.find((tag) => tag.tagId === tagId) ?? null;
    }

    toggleExpand(): void {
        this.isExpanded = !this.isExpanded;
    }

    private async load(): Promise<void> {
        if (!this.personId) return;

        const seq = ++this.requestSeq;
        this.loading = true;
        this.error = null;
        this.photoUrl = null;
        this.zoneName = null;
        this.activeTagCount = 0;
        this.typeId = null;
        this.tags = [];
        this.selectedTagId = null;
        this.selectedTag = null;
        this.isExpanded = false;
        this.activeBackTab = 'summary';

        try {
            const [detailsResult, mapResult] = await Promise.allSettled([
                firstValueFrom(
                    this.dataService.getAny<any>(`track-item/get-by-id/details/${this.personId}`)
                ),
                firstValueFrom(
                    this.dataService.getAny<TrackItemMapEntry[]>(`track-item/get-object-map/${this.personId}`)
                ),
            ]);

            if (seq !== this.requestSeq) return;
            if (detailsResult.status === 'rejected') throw detailsResult.reason;

            const response: any = detailsResult.value as any;
            const mapRows = mapResult.status === 'fulfilled' && Array.isArray(mapResult.value)
                ? (mapResult.value as TrackItemMapEntry[])
                : [];

            const nameFromApi = (response?.description || response?.displayName || '').toString().trim();
            if (!this.displayName && nameFromApi) {
                this.displayName = nameFromApi;
            }

            const photo = response?.photoUrl;
            if (photo) {
                this.photoUrl = this.imageService.getAdaptiveImage(photo, 0.8, 512);
            }

            this.zoneName = this.pickText(response?.zone?.name, mapRows[0]?.zoneName);

            const sessionTags = this.buildTagSeedsFromDetails(response);
            const mapTags = this.buildTagSeedsFromMap(mapRows);
            const tagSeeds = sessionTags.length > 0 ? sessionTags : mapTags;

            const tagPanels = await Promise.all(
                tagSeeds.map((tag) => this.buildTagPanel(tag, mapRows))
            );

            if (seq !== this.requestSeq) return;

            tagPanels.sort((a, b) => this.toTimestamp(b.lastSeenAt) - this.toTimestamp(a.lastSeenAt));
            this.tags = tagPanels;
            this.activeTagCount = this.tags.length;
            this.selectedTag = this.tags[0] ?? null;
            this.selectedTagId = this.selectedTag?.tagId ?? null;

            // Different endpoints may serialize this differently; support a few common shapes.
            this.typeId =
                response?.typeId ??
                response?.trackItemTypeId ??
                response?.type?.id ??
                null;
        } catch (err: any) {
            if (seq !== this.requestSeq) return;
            this.error = 'Profil bilgisi getirilemedi.';
        } finally {
            if (seq === this.requestSeq) {
                this.loading = false;
            }
        }
    }

    private buildTagSeedsFromDetails(details: any): TagSeed[] {
        const sessions = Array.isArray(details?.trackItemSessions) ? details.trackItemSessions : [];
        return sessions
            .map((session: any): TagSeed | null => {
                const tagId = this.pickText(session?.currentTagSession?.tagId, session?.currentTagSession?.tag?.id);
                if (!tagId) return null;
                return {
                    tagId,
                    tagName: this.pickText(session?.currentTagSession?.tag?.name),
                    tagCode: this.pickText(session?.currentTagSession?.tag?.value),
                };
            })
            .filter((item: TagSeed | null): item is TagSeed => !!item);
    }

    private buildTagSeedsFromMap(mapRows: TrackItemMapEntry[]): TagSeed[] {
        return (mapRows ?? [])
            .map((row): TagSeed | null => {
                const tagId = this.pickText(row?.tagId);
                if (!tagId) return null;
                return {
                    tagId,
                    tagName: this.pickText(row?.tagName),
                    tagCode: this.pickText(row?.tagCode),
                };
            })
            .filter((item: TagSeed | null): item is TagSeed => !!item);
    }

    private async buildTagPanel(tag: TagSeed, mapRows: TrackItemMapEntry[]): Promise<TagPanel> {
        const [historyResult, propertyResult] = await Promise.allSettled([
            this.loadTagHistory(tag.tagId),
            this.loadTagProperties(tag.tagId)
        ]);

        const historyFromApi = historyResult.status === 'fulfilled' ? historyResult.value : [];
        const properties = propertyResult.status === 'fulfilled' ? propertyResult.value : [];
        const fallbackHistory = this.loadFallbackHistoryFromMap(tag, mapRows);

        const history = (historyFromApi.length > 0 ? historyFromApi : fallbackHistory)
            .sort((a, b) => this.toTimestamp(b.lastSeenAt) - this.toTimestamp(a.lastSeenAt))
            .slice(0, 3);

        const latest = history[0] ?? null;

        return {
            tagId: tag.tagId,
            tagName: tag.tagName,
            tagCode: tag.tagCode,
            label: this.buildTagLabel(tag.tagName, tag.tagCode),
            lastLocationText: latest?.locationText ?? null,
            lastSeenAt: latest?.lastSeenAt ?? null,
            history,
            properties,
        };
    }

    private async loadTagHistory(tagId: string): Promise<TagHistoryEntry[]> {
        if (!tagId) return [];
        const rows: any = await firstValueFrom(this.dataService.post<any[]>(`access-history/get-by-tag-id/${tagId}`));
        const list = Array.isArray(rows) ? rows : [];
        return list.map((row) => this.mapHistoryEntry(row));
    }

    private async loadTagProperties(tagId: string): Promise<TagPropertyEntry[]> {
        if (!tagId) return [];
        const rows: any = await firstValueFrom(this.dataService.getV2<any[]>(`tags/${tagId}/properties`));
        const list = Array.isArray(rows) ? rows : [];
        return list.map((row) => this.mapPropertyEntry(row));
    }

    private loadFallbackHistoryFromMap(tag: TagSeed, mapRows: TrackItemMapEntry[]): TagHistoryEntry[] {
        const tagKey = this.normalizeId(tag.tagId);
        const codeKey = this.normalizeId(tag.tagCode);

        const filtered = (mapRows ?? []).filter((row) => {
            const rowTagKey = this.normalizeId(row?.tagId);
            if (tagKey && rowTagKey) {
                return rowTagKey === tagKey;
            }
            return codeKey && this.normalizeId(row?.tagCode) === codeKey;
        });

        return filtered.map((row) => {
            const zoneName = this.pickText(row?.zoneName);
            const floorName = this.pickText(row?.floorName);
            const buildingName = this.pickText(row?.buildingName);
            return {
                locationText: this.joinLocation([zoneName, floorName, buildingName]),
                zoneName,
                floorName,
                buildingName,
                firstSeenAt: null,
                lastSeenAt: this.pickText(row?.lastSeen),
                durationText: null,
            };
        });
    }

    private mapHistoryEntry(row: any): TagHistoryEntry {
        const zoneName = this.pickText(
            row?.readerAntenna?.gateDirection?.gate?.zone?.name,
            row?.zoneName
        );
        const floorName = this.pickText(
            row?.readerAntenna?.gateDirection?.gate?.zone?.floor?.name,
            row?.floorName
        );
        const buildingName = this.pickText(
            row?.readerAntenna?.gateDirection?.gate?.zone?.floor?.building?.name,
            row?.buildingName
        );
        const durationSec = this.toNumber(row?.zoneDuration);

        return {
            locationText: this.joinLocation([zoneName, floorName, buildingName]),
            zoneName,
            floorName,
            buildingName,
            firstSeenAt: this.pickText(row?.firstSeenAt),
            lastSeenAt: this.pickText(row?.lastSeenAt, row?.lastSeen),
            durationText: durationSec !== null ? this.formatDuration(durationSec) : null,
        };
    }

    private mapPropertyEntry(row: any): TagPropertyEntry {
        const current = row?.current ?? row;
        const prefix = this.pickText(row?.tagPropertyTemplatePrefix, row?.prefix) ?? '';
        const suffix = this.pickText(row?.tagPropertyTemplateSuffix, row?.suffix) ?? '';

        return {
            name: this.pickText(row?.tagPropertyTemplateName, row?.templateName, row?.name) ?? 'Property',
            value: this.resolvePropertyValue(current, prefix, suffix),
            readAt: this.pickText(current?.readAt, row?.readAt),
        };
    }

    private resolvePropertyValue(current: any, prefix: string, suffix: string): string {
        if (current?.booleanValue !== null && current?.booleanValue !== undefined) {
            return current.booleanValue ? 'Evet' : 'Hayir';
        }

        if (current?.numberValue !== null && current?.numberValue !== undefined) {
            return `${prefix}${current.numberValue}${suffix}`.trim();
        }

        if (current?.dateTimeValue) {
            return `${prefix}${current.dateTimeValue}${suffix}`.trim();
        }

        const text = this.pickText(current?.value, current?.stringValue, current?.textValue);
        if (text) {
            return `${prefix}${text}${suffix}`.trim();
        }

        return '-';
    }

    private buildTagLabel(name: string | null, code: string | null): string {
        if (name && code) return `${name} (${code})`;
        return name ?? code ?? 'Tag';
    }

    private normalizeId(value: any): string {
        return (value ?? '').toString().trim().toLowerCase();
    }

    private toTimestamp(value: string | null | undefined): number {
        if (!value) return 0;
        const parsed = new Date(value).getTime();
        return Number.isNaN(parsed) ? 0 : parsed;
    }

    private toNumber(value: any): number | null {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private formatDuration(totalSeconds: number): string {
        const safe = Math.max(0, Math.round(totalSeconds));
        const hours = Math.floor(safe / 3600);
        const minutes = Math.floor((safe % 3600) / 60);
        const seconds = safe % 60;

        if (hours > 0) return `${hours}sa ${minutes}dk`;
        if (minutes > 0) return `${minutes}dk ${seconds}sn`;
        return `${seconds}sn`;
    }

    private pickText(...values: any[]): string | null {
        for (const value of values) {
            const text = (value ?? '').toString().trim();
            if (text) {
                return text;
            }
        }
        return null;
    }

    private joinLocation(parts: Array<string | null | undefined>): string | null {
        const normalized = parts
            .map((part) => (part ?? '').toString().trim())
            .filter(Boolean);
        return normalized.length ? normalized.join(' / ') : null;
    }
}